import React, { useState, useEffect, useCallback } from 'react';
import {
  FiZap,
  FiBarChart2,
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiCheckCircle,
  FiStar,
  FiClock,
  FiBookOpen,
  FiAlertCircle,
  
} from 'react-icons/fi';

import LoadingSpinner from './LoadingSpinner';
import Button from './Button';
import Badge from './Badge';
import { aiAnalysisService } from '../../services/aiAnalysisService';

import './AIInsights.css';

const AIInsights = ({ testResults, testData, userAnswers, questions, insights: providedInsights }) => {
  const [insights, setInsights] = useState(providedInsights ?? null);
  const [loading, setLoading] = useState(!providedInsights);
  const [error, setError] = useState(null);

  const generateInsights = useCallback(async () => {
    try {
      if (!testResults || !testData || !questions) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      const analysisData = {
        testResults,
        testData,
        userAnswers: userAnswers || [],
        questions: questions || []
      };

      const aiInsights = await aiAnalysisService.generateInsights(analysisData);
      setInsights(aiInsights);
    } catch (err) {
      console.error('Error generating AI insights:', err);
      setError('Failed to generate insights. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [testResults, testData, userAnswers, questions]);

  useEffect(() => {
    if (providedInsights) {
      setInsights(providedInsights);
      setLoading(false);
      return;
    }
    generateInsights();
  }, [providedInsights, generateInsights]);

  const getPerformanceColor = (level) => {
    switch (level) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'average': return 'warning';
      case 'needs-improvement': return 'danger';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <div className="ai-insights-loading">
        <div className="loading-content">
          <FiZap className="ai-icon spinning" />
          <LoadingSpinner size="small" />
          <p>AI is analyzing your performance...</p>
          <span className="loading-subtitle">This may take a few moments</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-insights-error">
        <FiAlertCircle className="error-icon" />
        <h3>Unable to Generate Insights</h3>
        <p>{error}</p>
        <Button onClick={generateInsights} variant="outline" size="small">
          Try Again
        </Button>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="ai-insights-empty">
        <FiZap className="empty-icon" />
        <p>No insights available for this test.</p>
      </div>
    );
  }

  return (
    <div className="ai-insights">
      <div className="insights-header">
        <div className="header-icon">
          <FiZap className="ai-icon" />
        </div>
        <div className="header-content">
          <h2>AI Performance Insights</h2>
          <p>Personalized analysis powered by AI and syllabus collaboration</p>
        </div>
      </div>

      <div className="insights-grid">
        {/* Overall Performance */}
        <div className="insight-card overall-performance">
          <div className="card-header">
            <FiBarChart2 className="card-icon" />
            <h3>Overall Performance</h3>
          </div>
          <div className="performance-summary">
            <div className="performance-level">
              <Badge variant={getPerformanceColor(insights.overallPerformance?.level)}>
                {insights.overallPerformance?.level || 'Good'}
              </Badge>
              <span className="performance-score">{testResults.percentage}%</span>
            </div>
            <p className="performance-description">
              {insights.overallPerformance?.description || 
               `You scored ${testResults.percentage}% on this test, demonstrating solid understanding of the material.`}
            </p>
          </div>
        </div>

        {/* Strengths */}
        <div className="insight-card strengths">
          <div className="card-header">
            <FiTrendingUp className="card-icon success" />
            <h3>Your Strengths</h3>
          </div>
          <div className="strengths-list">
            {insights.strengths?.length > 0 ? (
              insights.strengths.map((strength, index) => (
                <div key={index} className="strength-item">
                  <FiCheckCircle className="strength-icon" />
                  <div className="strength-content">
                    <h4>{strength.topic || strength.area}</h4>
                    <p>{strength.description}</p>
                    {strength.score && (
                      <span className="strength-score">{strength.score}% mastery</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="default-strength">
                <FiStar className="strength-icon" />
                <div className="strength-content">
                  <h4>Problem Solving</h4>
                  <p>You demonstrated good analytical thinking in your approach to the questions.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Areas for Improvement */}
        <div className="insight-card improvements">
          <div className="card-header">
            <FiTrendingDown className="card-icon warning" />
            <h3>Areas for Improvement</h3>
          </div>
          <div className="improvements-list">
            {insights.improvements?.length > 0 ? (
              insights.improvements.map((improvement, index) => (
                <div key={index} className="improvement-item">
                  <FiTarget className="improvement-icon" />
                  <div className="improvement-content">
                    <h4>{improvement.topic || improvement.area}</h4>
                    <p>{improvement.description}</p>
                    {improvement.score && (
                      <span className="improvement-score">{improvement.score}% mastery</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="default-improvement">
                <FiClock className="improvement-icon" />
                <div className="improvement-content">
                  <h4>Time Management</h4>
                  <p>Consider practicing time management strategies to optimize your test-taking efficiency.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="insight-card recommendations full-width">
          <div className="card-header">
            <FiZap className="card-icon info" />
            <h3>Personalized Study Recommendations</h3>
          </div>
          <div className="recommendations-grid">
            {insights.recommendations?.length > 0 ? (
              insights.recommendations.map((rec, index) => (
                <div key={index} className="recommendation-item">
                  <div className="rec-header">
                    <FiBookOpen className="rec-icon" />
                    <div className="rec-title-section">
                      <h4>{rec.title || rec.topic}</h4>
                      {rec.timeEstimate && (
                        <span className="time-estimate">
                          <FiClock className="time-icon" />
                          {rec.timeEstimate}
                        </span>
                      )}
                    </div>
                  </div>
                  <p>{rec.description}</p>
                  
                  {rec.actionItems && rec.actionItems.length > 0 && (
                    <div className="action-items">
                      <span className="action-label">Action Steps:</span>
                      <ul className="action-list">
                        {rec.actionItems.map((action, idx) => (
                          <li key={idx}>
                            <FiCheckCircle className="action-icon" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {rec.resources && rec.resources.length > 0 && (
                    <div className="rec-resources">
                      <span className="resources-label">Study Resources:</span>
                      <ul>
                        {rec.resources.map((resource, idx) => (
                          <li key={idx}>{resource}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="rec-footer">
                    {rec.priority && (
                      <Badge variant={rec.priority === 'high' ? 'danger' : rec.priority === 'medium' ? 'warning' : 'info'}>
                        {rec.priority} priority
                      </Badge>
                    )}
                    {rec.difficulty && (
                      <Badge variant="outline">
                        {rec.difficulty} level
                      </Badge>
                    )}
                    {rec.expectedImpact && (
                      <span className="expected-impact">
                        <FiTrendingUp className="impact-icon" />
                        {rec.expectedImpact}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="default-recommendations">
                <div className="recommendation-item">
                  <div className="rec-header">
                    <FiBookOpen className="rec-icon" />
                    <h4>Review Core Concepts</h4>
                  </div>
                  <p>Focus on reviewing the fundamental concepts covered in this test to strengthen your foundation.</p>
                </div>
                <div className="recommendation-item">
                  <div className="rec-header">
                    <FiTarget className="rec-icon" />
                    <h4>Practice Similar Questions</h4>
                  </div>
                  <p>Take additional practice tests to reinforce your understanding and improve your performance.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Performance Patterns */}
        {insights.detailedAnalysis?.performancePatterns && insights.detailedAnalysis.performancePatterns.length > 0 && (
          <div className="insight-card performance-patterns full-width">
            <div className="card-header">
              <FiBarChart2 className="card-icon warning" />
              <h3>Performance Patterns</h3>
            </div>
            <div className="patterns-list">
              {insights.detailedAnalysis.performancePatterns.map((pattern, index) => (
                <div key={index} className="pattern-item">
                  <div className="pattern-header">
                    <FiAlertCircle className="pattern-icon" />
                    <h4>{pattern.description}</h4>
                  </div>
                  <p className="pattern-recommendation">{pattern.recommendation}</p>
                  <Badge variant="info">{pattern.type} pattern</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Topic Breakdown */}
        {insights.detailedAnalysis?.topicBreakdown && insights.detailedAnalysis.topicBreakdown.length > 0 && (
          <div className="insight-card topic-breakdown full-width">
            <div className="card-header">
              <FiTarget className="card-icon info" />
              <h3>Detailed Topic Analysis</h3>
            </div>
            <div className="topic-breakdown-grid">
              {insights.detailedAnalysis.topicBreakdown.slice(0, 6).map((topic, index) => (
                <div key={index} className="topic-breakdown-item">
                  <div className="topic-header">
                    <h4>{topic.topic}</h4>
                    <Badge variant={getPerformanceColor(topic.performanceLevel)}>
                      {topic.score}%
                    </Badge>
                  </div>
                  <div className="topic-stats">
                    <span className="stat">
                      <FiCheckCircle className="stat-icon" />
                      {topic.correct}/{topic.total} correct
                    </span>
                    {topic.studyPriority && (
                      <Badge variant={topic.studyPriority === 'high' ? 'danger' : topic.studyPriority === 'medium' ? 'warning' : 'success'}>
                        {topic.studyPriority} priority
                      </Badge>
                    )}
                  </div>
                  {topic.difficultyBreakdown && Object.keys(topic.difficultyBreakdown).length > 0 && (
                    <div className="difficulty-breakdown">
                      <span className="breakdown-label">By difficulty:</span>
                      <div className="difficulty-stats">
                        {Object.entries(topic.difficultyBreakdown).map(([difficulty, stats]) => (
                          <span key={difficulty} className={`difficulty-stat ${stats.needsWork ? 'needs-work' : ''}`}>
                            {difficulty}: {stats.percentage}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Study Efficiency Score */}
        {insights.detailedAnalysis?.studyEfficiencyScore && (
          <div className="insight-card study-efficiency">
            <div className="card-header">
              <FiZap className="card-icon success" />
              <h3>Study Efficiency</h3>
            </div>
            <div className="efficiency-content">
              <div className="efficiency-score">
                <span className="score-value">{insights.detailedAnalysis.studyEfficiencyScore}%</span>
                <span className="score-label">Efficiency Score</span>
              </div>
              <p className="efficiency-description">
                {insights.detailedAnalysis.studyEfficiencyScore >= 80 
                  ? "Your study efforts are well-focused. Continue with your current approach."
                  : insights.detailedAnalysis.studyEfficiencyScore >= 60
                  ? "Good focus, but there's room for improvement in study prioritization."
                  : "Consider restructuring your study plan to focus on high-impact areas first."
                }
              </p>
            </div>
          </div>
        )}

        {/* Next Steps */}
        {insights.nextSteps && insights.nextSteps.length > 0 && (
          <div className="insight-card next-steps full-width">
            <div className="card-header">
              <FiTarget className="card-icon primary" />
              <h3>Your Next Steps</h3>
            </div>
            <div className="next-steps-list">
              {insights.nextSteps.map((step, index) => (
                <div key={index} className="next-step-item">
                  <div className="step-number">{index + 1}</div>
                  <div className="step-content">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                    {step.timeframe && (
                      <span className="step-timeframe">Timeframe: {step.timeframe}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="insights-footer">
        <div className="ai-disclaimer">
          <FiZap className="disclaimer-icon" />
          <p>
            These insights are generated by AI based on your test performance and syllabus analysis. 
            Use them as guidance alongside your teacher's feedback and curriculum requirements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIInsights;